import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

import { api, ApiError } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { PurchasePartnerHit } from '@/types/models'
import { inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'

export interface BulkResult { created: number; updated: number }

/** 여러 거래처에 한 번에 저장(…/bulk). 이미 있는 거래처가 있으면(409) 확인 후 덮어쓰기.
 *  취소하면 null. */
export async function postToPartners(apiBase: string, partners: number[],
                                     body: Record<string, unknown>): Promise<BulkResult | null> {
  const send = (overwrite: boolean) => api<BulkResult>(`${apiBase}/bulk${overwrite ? '?overwrite=1' : ''}`,
    { method: 'POST', body: { ...body, partners } })
  try {
    return await send(false)
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 409) throw e
    if (!await confirm(`${e.detail}\n\n이 거래처들은 새 값으로 덮어쓸까요?`)) return null
    return send(true)
  }
}

interface Props {
  value: PurchasePartnerHit[]
  onChange: (v: PurchasePartnerHit[]) => void
  placeholder?: string
}

/** 전사(회사무관) 거래처 여러 개 선택 — 구매장/라벨 매핑·별칭 '추가' 전용(고객 요청 2026-10-14).
 *  체크로 고르고, 검색 결과 [전체 선택]으로 같은 이름 지점을 한 번에. 고른 거래처는 위에 태그. */
export function PurchasePartnerMultiCombo({ value, onChange, placeholder = '거래처 검색...' }: Props) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<PurchasePartnerHit[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      api<{ rows: PurchasePartnerHit[]; truncated: boolean }>(`/api/purchase/partners?q=${encodeURIComponent(q)}`)
        .then((r) => { if (!cancelled) { setHits(r.rows); setTruncated(r.truncated) } })
        .catch(() => { if (!cancelled) { setHits([]); setTruncated(false) } })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [q, open])

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const picked = new Set(value.map((p) => p.id))
  const toggle = (h: PurchasePartnerHit) =>
    onChange(picked.has(h.id) ? value.filter((p) => p.id !== h.id) : [...value, h])
  const allPicked = hits.length > 0 && hits.every((h) => picked.has(h.id))
  const toggleAll = () => onChange(allPicked
    ? value.filter((p) => !hits.some((h) => h.id === p.id))
    : [...value, ...hits.filter((h) => !picked.has(h.id))])

  return (
    <div ref={boxRef} className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5 max-h-28 overflow-auto">
          {value.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5">
              {p.name}
              <button type="button" onClick={() => toggle(p)} className="text-emerald-500 hover:text-rose-600">
                <X className="w-3 h-3"/>
              </button>
            </span>
          ))}
          <button type="button" onClick={() => onChange([])}
                  className="text-xs text-slate-400 hover:text-slate-700 px-1">모두 지우기</button>
        </div>
      )}
      <button type="button" onClick={() => setOpen((v) => !v)}
              className={cn(inputCls, 'text-left', !value.length && 'text-slate-400')}>
        {value.length ? `${value.length}곳 선택됨 — 더 추가하려면 검색` : placeholder}
      </button>
      {open && (
        // 창 안에 떠 있는 작은 목록이면 몇 줄밖에 안 보여(고객 요청 2026-10-15 '검색창 크게')
        // 아래로 펼쳐 창을 키우고 목록은 화면 높이의 60%까지.
        <div className="mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-sm max-h-[60vh] overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-slate-100 flex items-center gap-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}                   placeholder="회사/거래처 검색" className={inputCls + ' text-sm flex-1'}/>
            <button type="button" onClick={toggleAll} disabled={!hits.length}
                    className="text-xs font-semibold border border-slate-300 rounded-lg px-2 py-1.5 whitespace-nowrap hover:bg-slate-50 disabled:opacity-40">
              {allPicked ? '전체 해제' : `전체 선택 (${hits.length})`}
            </button>
            <button type="button" onClick={() => setOpen(false)}
                    className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5 py-1.5 whitespace-nowrap">
              완료
            </button>
          </div>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">검색 중...</div>}
          {!loading && !hits.length && (
            <div className="px-3 py-2 text-xs text-slate-400">검색 결과 없음</div>
          )}
          {hits.map((h) => (
            <label key={h.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-emerald-50 cursor-pointer">
              <input type="checkbox" checked={picked.has(h.id)} onChange={() => toggle(h)}
                     className="accent-emerald-600"/>
              <span className="text-[11px] text-slate-400">{h.company_name}</span>
              <span className="font-mono text-xs text-slate-400">{h.code}</span>
              {h.name}
            </label>
          ))}
          {truncated && (
            <div className="px-3 py-1.5 text-[11px] text-amber-600 bg-amber-50 border-t border-amber-100">
              결과가 많습니다 — 거래처명을 더 입력해 좁혀주세요.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
